import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import QRCode from 'react-qr-code';
import { Survey, TableSurveysRow } from './TableSurveysRow';
import { Order, getComparator, EnhancedTableHead } from './TableUtils';
import Fab from '@mui/material/Fab';
import InsightsIcon from '@mui/icons-material/Insights';
import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import { SplitButton } from './SplitButton';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import ExcelUploadAndParse from './ExcelUploadAndParse';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { EXECUTIVE_SESSION_LABEL, EXECUTIVE_SESSION_TYPE, SURVEY_MAPPING } from '../Constants';

interface TableSurveyProps {
  rows: Survey[];
  analyzeSurvey: (surveyId: string | number) => void;
  loading: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  loadingParticipants: string | number;
  isMobile: boolean;
  analyzeSurveys: (surveys: Survey[], name: string) => void;
  analyzeSurveysLoading: boolean;
  setSurveys: (surveys: Survey[]) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
  setLeaderBySurvey: (leaderBySurvey: any) => void;
  editLeaderReportDate: (
    survey: Survey,
    leaderMail: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => Promise<void>;
  createSurvey: (
    originalSurveyId: string,
    newTitle: string,
    leaderMail: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null,
    participants?: string[],
    surveyType?: string
  ) => Promise<unknown>;
  multipleCreateSurvey: (data: Array<unknown>) => Promise<unknown[]>;
  sendExecutiveInvites: (
    surveyId: string | number,
    surveyTitle: string
  ) => Promise<{ sent: number; failed: string[]; invitesSentAt?: string }>;
  /** True while the initial survey list fetch is in flight; suppresses the empty-state message. */
  retrievalInProgress: boolean;
  /** Shows the admin-only Owner column. */
  isAdmin: boolean;
}

/** Loose sanity check only — the address is validated for real by SES on send. */
const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const TableSurveys: React.FC<TableSurveyProps> = ({
  rows,
  analyzeSurvey,
  loading,
  getParticipants,
  participantsBySurvey,
  loadingParticipants,
  isMobile,
  analyzeSurveys,
  analyzeSurveysLoading,
  setSurveys,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
  setLeaderBySurvey,
  editLeaderReportDate,
  createSurvey,
  multipleCreateSurvey,
  sendExecutiveInvites,
  retrievalInProgress,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('id');
  const [page, setPage] = React.useState<number>(0);
  const [titleFilter, setTitleFilter] = React.useState<string>('');
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [qrModalOpen, setQrModalOpen] = React.useState<string>('');
  const [selected, setSelected] = React.useState<Survey[]>([]);
  const [creationInProgress, setCreationInProgress] = React.useState<string>('');
  const [newTitle, setNewTitle] = React.useState<string>('');
  const [surveysAnalysisName, setSurveysAnalysisName] = React.useState<string>('');
  const [teamOfTeamsModalOpen, setTeamOfTeamsModalOpen] = React.useState<boolean>(false);
  const [leaderMail, setLeaderMail] = React.useState<string>('');
  const [participants, setParticipants] = React.useState<string[]>([]);
  const [reportDate, setReportDate] = React.useState<Dayjs | null>(null);
  const [reminderDate, setReminderDate] = React.useState<Dayjs | null>(null);
  const [open, setOpen] = React.useState(false);
  const [multipleCreationInProgress, setMultipleCreationInProgress] = React.useState(false);

  const handleCreateSurvey = async (surveyName: string) => {
    const originalSurveyId = SURVEY_MAPPING[surveyName];
    const isExecutiveSession = surveyName === EXECUTIVE_SESSION_LABEL;
    setCreationInProgress(originalSurveyId);
    if (!newTitle) {
      alert(t('surveys.specify_title'));
      setCreationInProgress('');
      return;
    }
    if (isExecutiveSession && participants.some((e) => !isValidEmail(e))) {
      alert(t('surveys.invalid_participants'));
      setCreationInProgress('');
      return;
    }
    try {
      const output = (await createSurvey(
        originalSurveyId,
        newTitle,
        leaderMail,
        reportDate,
        reminderDate,
        isExecutiveSession ? participants : [],
        isExecutiveSession ? EXECUTIVE_SESSION_TYPE : undefined
      )) as any;
      setSurveys([output, ...rows]);
      setLeaderBySurvey({
        ...leaderBySurvey,
        [output['i']]: {
          leader: leaderMail,
          reportDate: reportDate ? reportDate.format('YYYY-MM-DD') : null,
          reminderDate: reminderDate ? reminderDate.format('YYYY-MM-DD') : null,
        },
      });
      setParticipants([]);
      setCreationInProgress('');
    } catch (error) {
      console.error('An error occurred:', error);
      setCreationInProgress('');
    }
  };

  const handleMultipleCreateSurvey = async (data: Array<any>) => {
    setMultipleCreationInProgress(true);
    try {
      const output = (await multipleCreateSurvey(data)) as any[];
      setSurveys([...output, ...rows]);
      let newLeaderBySurvey: { [key: string]: any } = {};
      for (const s of output) {
        newLeaderBySurvey[s['i']] = {
          leader: s['leader'],
          reportDate: s['reportDate'],
          reminderDate: s['reminderDate'],
        };
      }
      setLeaderBySurvey({ ...leaderBySurvey, ...newLeaderBySurvey });
      setMultipleCreationInProgress(false);
    } catch (error) {
      console.error('An error occurred:', error);
      setMultipleCreationInProgress(false);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelect = (survey: Survey) => {
    const selectedIndex = selected.map((s) => s.i).indexOf(survey.i);
    let newSelected: Survey[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, survey);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .filter((s) => s.t?.toLowerCase().includes(titleFilter))
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows, titleFilter]
  );
  const horizontal = window.innerHeight < window.innerWidth;
  // Always render (even with zero surveys) so the create section stays available —
  // non-admin users start with an empty list.
  return (
    <Box>
      <Dialog
        fullScreen={true}
        maxWidth={false}
        open={qrModalOpen !== ''}
        onClose={() => setQrModalOpen('')}
        aria-labelledby="responsive-dialog-title"
      >
        <div style={{ background: '#fafafa', padding: '16px' }}>
          <div
            style={{
              height: horizontal ? '88vh' : 'auto',
              margin: '0 auto',
              width: horizontal ? 'auto' : '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <QRCode
              size={256}
              style={{
                maxHeight: '100%',
                height: horizontal ? '88vh' : 'auto',
                maxWidth: '100%',
                width: horizontal ? 'auto' : '100%',
              }}
              value={qrModalOpen}
              viewBox={`0 0 256 256`}
            />
          </div>
        </div>
        <DialogActions>
          <Button onClick={() => setQrModalOpen('')}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={teamOfTeamsModalOpen}
        onClose={() => setTeamOfTeamsModalOpen(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>{t('surveys.team_of_teams_dialog_title')}</DialogTitle>
        <Grid
          padding={4}
          spacing={4}
          container
          direction="row"
          sx={{ justifyContent: 'flex-start', alignItems: 'center' }}
        >
          <Input
            id="newTitle"
            value={surveysAnalysisName}
            placeholder={t('surveys.team_of_teams_name')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSurveysAnalysisName(e.target.value)
            }
          />
          <Button
            onClick={() => analyzeSurveys(selected, surveysAnalysisName)}
            disabled={analyzeSurveysLoading}
          >
            {analyzeSurveysLoading ? <CircularProgress /> : t('common.analyze')}
          </Button>
        </Grid>
        <DialogActions>
          <Button onClick={() => setTeamOfTeamsModalOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
      <Toolbar
        sx={[
          {
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          },
        ]}
      >
        <Typography variant="h5" id="createTitle" component="div" textAlign={'initial'}>
          {t('surveys.create')}
        </Typography>
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={() => {
            setOpen(!open);
          }}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Toolbar>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Paper>
          <Grid
            spacing={4}
            padding={4}
            marginBottom={4}
            container
            direction="row"
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Input
              id="newTitle"
              value={newTitle}
              placeholder={t('surveys.new_title')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTitle(e.target.value)}
            />
            <FormControl>
              <Input
                id="leader"
                value={leaderMail}
                placeholder={t('surveys.leader_email')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeaderMail(e.target.value)}
              />
              <FormHelperText>{t('surveys.leader_email_hint')}</FormHelperText>
            </FormControl>
            <FormControl sx={{ minWidth: 320 }}>
              <Autocomplete
                multiple
                freeSolo
                id="participants"
                options={[] as string[]}
                value={participants}
                onChange={(_, value) =>
                  setParticipants(
                    (value as string[]).map((e) => e.trim().toLowerCase()).filter(Boolean)
                  )
                }
                renderTags={(value: readonly string[], getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      size="small"
                      label={option}
                      color={isValidEmail(option) ? 'default' : 'error'}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder={t('surveys.participants_email')}
                  />
                )}
              />
              <FormHelperText>{t('surveys.participants_email_hint')}</FormHelperText>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                label={t('surveys.reminder_date')}
                value={reminderDate}
                onChange={(v) => setReminderDate(v)}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                label={t('surveys.report_date')}
                value={reportDate}
                onChange={(v) => setReportDate(v)}
              />
            </LocalizationProvider>
            {creationInProgress ? (
              <CircularProgress />
            ) : (
              <SplitButton
                disabled={newTitle === ''}
                options={Object.keys(SURVEY_MAPPING)}
                prefix={'Create '}
                callback={handleCreateSurvey}
              />
            )}
          </Grid>
        </Paper>
        <ExcelUploadAndParse
          mode="surveys"
          loading={multipleCreationInProgress}
          onSubmit={handleMultipleCreateSurvey}
        />
      </Collapse>
      <Toolbar
        sx={[
          {
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          },
        ]}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h5"
          id="tableTitle"
          component="div"
          textAlign={'initial'}
        >
          {t('nav.surveys')}
        </Typography>
        <Input
          id="titleFilter"
          value={titleFilter}
          placeholder={t('common.filter_by_title')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleFilter(e.target.value)}
        />
      </Toolbar>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={'small'}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            showOwner={isAdmin}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              return (
                <TableSurveysRow
                  key={index}
                  row={row}
                  analyzeSurvey={analyzeSurvey}
                  loading={loading}
                  getParticipants={getParticipants}
                  participantsBySurvey={participantsBySurvey}
                  setQrModalOpen={setQrModalOpen}
                  loadingParticipants={loadingParticipants}
                  selectedSurveys={selected}
                  handleSelect={handleSelect}
                  isMobile={isMobile}
                  editLeaderReportDate={editLeaderReportDate}
                  getLeaderAndReportDate={getLeaderAndReportDate}
                  loadingLeader={loadingLeader}
                  leaderBySurvey={leaderBySurvey}
                  showOwner={isAdmin}
                  sendExecutiveInvites={sendExecutiveInvites}
                />
              );
            })}
            {rows.length === 0 && !retrievalInProgress && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('surveys.empty_list')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 33 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {selected.length > 0 && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: '24px', right: '24px' }}
          onClick={() => setTeamOfTeamsModalOpen(true)}
          disabled={analyzeSurveysLoading}
        >
          {analyzeSurveysLoading ? <CircularProgress /> : <InsightsIcon />}
        </Fab>
      )}
    </Box>
  );
};
